import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

type Props = {
  text: string;
};

export const InfoTooltip = ({ text }: Props) => {
  const [visible, setVisible] = useState(false);
  const [tooltipPos, setTooltipPos] = useState({ top: 0, left: 0 });
  const iconRef = useRef<HTMLSpanElement>(null);
  const containerRef = useRef<HTMLSpanElement>(null);

  const showTooltip = () => {
    if (iconRef.current) {
      const rect = iconRef.current.getBoundingClientRect();
      const TOOLTIP_WIDTH = 220;
      const MARGIN = 12;
      // Anchor left to icon, but clamp so it never overflows the right edge
      const left = Math.min(rect.left, window.innerWidth - TOOLTIP_WIDTH - MARGIN);
      setTooltipPos({ top: rect.bottom + 6, left });
    }
    setVisible(true);
  };

  // Dismiss on outside tap (mobile)
  useEffect(() => {
    if (!visible) return;
    const handleOutsideTouch = (e: TouchEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setVisible(false);
      }
    };
    document.addEventListener('touchstart', handleOutsideTouch);
    return () => document.removeEventListener('touchstart', handleOutsideTouch);
  }, [visible]);

  return (
    <span
      ref={containerRef}
      style={{ display: 'inline-flex', alignItems: 'center' }}
      onMouseEnter={showTooltip}
      onMouseLeave={() => setVisible(false)}
      onTouchStart={(e) => {
        e.stopPropagation();
        if (visible) {
          setVisible(false);
        } else {
          showTooltip();
        }
      }}
    >
      {/* ⓘ icon */}
      <span
        ref={iconRef}
        aria-label="More information"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 20,
          height: 20,
          borderRadius: '50%',
          border: '1.5px solid #aaa',
          fontSize: 12,
          fontWeight: 700,
          color: '#aaa',
          cursor: 'pointer',
          lineHeight: 1,
          flexShrink: 0,
          userSelect: 'none',
        }}
      >
        i
      </span>

      {/* Tooltip rendered in a portal so it's never clipped by any parent */}
      {visible && createPortal(
        <span
          style={{
            position: 'fixed',
            top: tooltipPos.top,
            left: tooltipPos.left,
            background: '#2a2a2a',
            color: '#fff',
            fontSize: 12,
            lineHeight: 1.5,
            padding: '8px 12px',
            borderRadius: 8,
            maxWidth: 220,
            width: 'max-content',
            boxShadow: '0 4px 16px rgba(0,0,0,0.25)',
            zIndex: 9999,
            pointerEvents: 'none',
            whiteSpace: 'normal',
            textAlign: 'left',
          }}
        >
          {text}
        </span>,
        document.body
      )}
    </span>
  );
};
